import {Injectable, isDevMode} from '@angular/core';
import {HttpClient} from "@angular/common/http";
import {StateService} from "@uirouter/core";

import {AppAlertService} from "../app-alert.service";
import {FormsService} from "../forms.service";
import {ParametersService} from "../parameters.service";
import {SubtitlesService} from "../subtitles.service";
import {UpdateService} from "../update.service";
import {UserService} from "../user.service";
import {CountryData} from "../../models";
import {ZipCodePipe} from "../../shared";

const MAX_MODULES_PER_ACCOUNT = 25;

@Injectable({
    providedIn: 'root'
})
export class RegistrationNewModuleService {
    acceptPostcodePolicy: boolean = false;
    additionalInformation: string = '';
    disabledRegisterButtons: boolean = false;
    email: string = '';
    moduleInfo: string = '';
    registerCode: string = '';
    selectedCountry: CountryData = {
        initial: null,
        name: null
    };
    validAdditionalInformation: boolean = true;
    validEmail: boolean = true;
    validModuleInfo: boolean | null = null;
    validRegisterCode: boolean | null = null;
    validZipCode: boolean | null = null;
    zipCode: string = '';

    constructor(
        private appAlert: AppAlertService,
        private http: HttpClient,
        private forms: FormsService,
        private parameters: ParametersService,
        private $state: StateService,
        private subtitles: SubtitlesService,
        private user: UserService,
        private update: UpdateService,
        private zipCodePipe: ZipCodePipe,
    ) {
        this.acceptPostcodePolicy = this.parameters.urlCompany === 'polypipe';
    }

    clearModuleRegister(): void {
        this.forms.captcha = '';
        this.forms.showCountry = false;
        this.forms.validCaptcha = null;

        this.acceptPostcodePolicy = this.parameters.urlCompany === 'polypipe';
        this.additionalInformation = '';
        this.disabledRegisterButtons = false;
        this.email = '';
        this.moduleInfo = '';
        this.registerCode = '';
        this.selectedCountry = {
            initial: null,
            name: null
        };
        this.validAdditionalInformation = true;
        this.validEmail = true;
        this.validModuleInfo = null;
        this.validRegisterCode = null;
        this.validZipCode = null;
        this.zipCode = '';
    }

    private clearCodes(): void {
        this.registerCode = '';
        this.validRegisterCode = null;
        this.forms.captcha = '';
        this.forms.validCaptcha = null;
        this.disabledRegisterButtons = false;
    }

    validateAdditionalInformation(): void {
        const regex = /^(.{0,40})$/;
        this.validAdditionalInformation = this.additionalInformation.length === 0
            ? true
            : regex.test(this.additionalInformation);
    }

    private validCountry(): boolean {
        return !!this.selectedCountry.initial && !!this.selectedCountry.name;
    }

    validateEmail(): void {
        const regex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        this.validEmail = (this.email != undefined && this.email.length == 0) ? true : regex.test(this.email);
    }

    validateModuleInfo(): void {
        const regex = /^(.{2,40})$/;
        this.validModuleInfo = regex.test(this.moduleInfo);
    }

    validateRegisterCode(): void {
        const regex = /^([0-9]{5})$/;
        this.validRegisterCode = regex.test(this.registerCode);
    }

    validateZipCode(): void {
        let regex: RegExp | null = null;
        if (this.acceptPostcodePolicy) {
            if (this.selectedCountry.initial === 'pl') {
                this.zipCode = this.zipCodePipe.transform(this.zipCode);
                regex = /^\d{2}(?:[-])\d{3}$/;
                this.validZipCode = regex.test(this.zipCode);
            } else {
                regex = /^([a-zA-Z0-9.-_\s]{4,10})$/;
                this.validZipCode = regex.test(this.zipCode);
            }
        } else {
            this.validZipCode = true;
        }
    }

    isModuleLimitReached(): boolean {
        return Array.isArray(this.parameters.modules)
            && this.parameters.modules.length >= MAX_MODULES_PER_ACCOUNT;
    }

    prepareRegistrationForm(): boolean {
        if (this.isModuleLimitReached()) {
            this.showModuleLimitAlert();
            return false;
        }

        this.clearModuleRegister();
        return true;
    }

    registerModule(): void {
        if (this.isModuleLimitReached()) {
            this.showModuleLimitAlert();
            return;
        }

        this.validateModuleInfo();
        this.validateRegisterCode();
        this.validCountry();
        this.validateZipCode();
        this.validateAdditionalInformation();
        this.validateEmail();
        if(isDevMode()){
            this.forms.validCaptcha = true;
        } else {
            this.forms.validateCaptcha();
        }

        let errorsNumber = 0;
        const messages: string[] = [];

        if (!this.validModuleInfo) {
            errorsNumber += 1;
            messages.push(`${errorsNumber}. ${this.subtitles.setSubtitle(3382)}`);
        }
        if (!this.validRegisterCode) {
            errorsNumber += 1;
            messages.push(`${errorsNumber}. ${this.subtitles.setSubtitle(3383)}`);
        }
        if (this.acceptPostcodePolicy && !this.validCountry()) {
            errorsNumber += 1;
            messages.push(`${errorsNumber}. ${this.subtitles.setSubtitle(6565)}`);
        }
        if (!this.validZipCode) {
            errorsNumber += 1;
            messages.push(`${errorsNumber}. ${this.subtitles.setSubtitle(3384)}`);
        }
        if (!this.validEmail) {
            errorsNumber += 1;
            messages.push(`${errorsNumber}. ${this.subtitles.setSubtitle(1342)}\n>`);
        }
        if (!this.validAdditionalInformation) {
            errorsNumber += 1;
            messages.push(`${errorsNumber}. ${this.subtitles.setSubtitle(1643)} ${this.subtitles.setSubtitle(2972)}`);
        }
        if (!this.forms.validCaptcha) {
            errorsNumber += 1;
            messages.push(`${errorsNumber}. ${this.subtitles.setSubtitle(8551)}`);
        }
        if (errorsNumber > 0) {
            this.appAlert.openAlertDialog({
                title: this.subtitles.setSubtitle(1183),
                message: messages,
                okButtonText: this.subtitles.setSubtitle(1136),
            });
        } else if (this.disabledRegisterButtons !== true) {
            this.update.stopInterval();
            this.disabledRegisterButtons = true;

            if (!this.acceptPostcodePolicy) {
                this.zipCode = '';
                this.selectedCountry = {
                    initial: null,
                    name: null,
                };
            }

            const data = {
                module_name: this.moduleInfo,
                registration_code: this.registerCode,
                notification_email: this.email,
                additional_information: this.additionalInformation,
                accept_post_policy: this.acceptPostcodePolicy,
                country: this.selectedCountry.name,
                zip_code: this.zipCode,
            };

            this.http.post(`/api/v1/users/${this.user.user_id}/module/registration`, data)
                .subscribe({
                    next: (response: any) => {
                        if (response.status === 'success') {
                            let message: string;
                            // message = subtitles.setSubtitle(4467); // do crawlera napisów, nie usuwać
                            if (this.subtitles.language === 'pl') {
                                message = `Trwa rejestrowanie modułu, proszę czekać.`;
                            } else if (this.subtitles.language === 'de') {
                                message = `Registrierung läuft, bitte warten.`;
                            } else {
                                message = `Registration in progress, please wait.`;
                            }
                            this.appAlert.showProgressBar(message, 20, this.registrationSuccess.bind(this));
                        }
                        if (response.status === 'fail' && response.code === -1) { // invalid register code
                            this.appAlert.openAlertDialog({
                                title: this.subtitles.setSubtitle(1183),
                                message: `${this.subtitles.setSubtitle(1327)}. ${this.subtitles.setSubtitle(1187)}`,
                                okButtonText: this.subtitles.setSubtitle(1136),
                            });
                            this.clearCodes();
                        }
                        if (response.status === 'fail' && response.code === -2) { // register code error
                            this.appAlert.openAlertDialog({
                                title: this.subtitles.setSubtitle(1183),
                                message: `${this.subtitles.setSubtitle(1327)}.</br> ${this.subtitles.setSubtitle(1190)}`,
                                okButtonText: this.subtitles.setSubtitle(1136),
                            });
                            this.clearCodes();
                        }
                        if (response.status === 'fail' && response.code === -4) { // module limit exceeded
                            this.showModuleLimitAlert();
                            this.clearCodes();
                        }
                    },
                    error: () => {
                        this.clearModuleRegister();
                        this.appAlert.openAlertDialog({
                            title: this.subtitles.setSubtitle(1183),
                            message: this.subtitles.setSubtitle(1941),
                            okButtonText: this.subtitles.setSubtitle(5015),
                        });
                    }
                });
        }
    }

    private showModuleLimitAlert(): void {
        this.appAlert.openAlertDialog({
            title: this.subtitles.setSubtitle(1183),
            message: this.subtitles.setSubtitle(18058),
            okButtonText: this.subtitles.setSubtitle(5015),
        });
    }

    private registrationSuccess(): void {
        let message = `${this.subtitles.setSubtitle(1328)}. ${this.subtitles.setSubtitle(1326)}.`;
        this.appAlert.openAlertDialog({
            title: this.subtitles.setSubtitle(1183),
            message,
            okButtonText: this.subtitles.setSubtitle(475),
            okFunction: this.reloadApp.bind(this),
        });
    }

    private reloadApp(): void {
        this.$state.go('home', {selectedModuleHash: 0}).then(
            () => {
                window.location.reload()
            }
        );
    }
}
